import { useState, useMemo } from "react";
import {
  TableSortLabel,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Box,
  Stack,
  Pagination,
  Tooltip,
  IconButton,
} from "@mui/material";
import { useSelector } from "react-redux";
import { QueryResultsSkeleton } from "./QueryResultsSkeleton";
import dayjs from "dayjs";
import DownloadIcon from "@mui/icons-material/Download";
import { setDisplayErrorSnackBar } from "../../../../features/userSlice";
import store from "../../../../store";

const DataStoreRequestRunQueryResults = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState<string>("");
  const [order, setOrder] = useState<"asc" | "desc">("asc");
  const [loading, setLoading] = useState(false);
  const { queryOutputTableData, runQueryButtonLoading } = useSelector(
    (state: any) => state.dataStoreReq,
  );

  // Handle case where queryOutputTableData might be undefined
  const { meta_data, query_output, output_metadata } = queryOutputTableData || {
    meta_data: { rowcount: 0 },
    query_output: [],
    output_metadata: {},
  };

  // Generate table headers from output_metadata titles
  const tableHeaders = useMemo(() => {
    return Object.entries(output_metadata)
      .map(([key, metadata]: any) => ({
        key,
        title: metadata.title || key,
        type: metadata.type,
        order: metadata.order,
      }))
      .sort((a, b) => parseInt(a.order) - parseInt(b.order));
  }, [output_metadata]);

  // Sort data if needed
  const sortedRows = useMemo(() => {
    if (!orderBy) return query_output;

    return [...query_output].sort((a: any, b: any) => {
      const aValue = a[orderBy];
      const bValue = b[orderBy];

      // Handle null/undefined values
      if (aValue == null) return order === "asc" ? -1 : 1;
      if (bValue == null) return order === "asc" ? 1 : -1;

      if (aValue < bValue) return order === "asc" ? -1 : 1;
      if (aValue > bValue) return order === "asc" ? 1 : -1;
      return 0;
    });
  }, [query_output, orderBy, order]);

  // Calculate total pages
  const totalPages = Math.ceil(meta_data.rowcount / rowsPerPage);

  // Paginated data
  const paginatedRows = useMemo(() => {
    return sortedRows.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage,
    );
  }, [sortedRows, page, rowsPerPage]);

  const handleSort = (columnKey: string) => {
    const isAsc = orderBy === columnKey && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(columnKey);
    setPage(0); // Reset to first page when sorting
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    // Ensure maximum of 20 rows per page
    setRowsPerPage(Math.min(newRowsPerPage, 20));
    setPage(0);
  };

  const getCellAlignment = (type: string) => {
    return type === "number" ? "right" : "left";
  };

  const formatCellValue = (value: any, type: string) => {
    if (value === null || value === undefined) return "N/A";

    if (type === "number") {
      return typeof value === "number" ? value.toLocaleString() : value;
    }

    return value;
  };

  // Handle empty results
  if (runQueryButtonLoading) {
    <QueryResultsSkeleton rowCount={5} columnCount={4} />;
  } else if (meta_data.rowcount === 0) {
    return (
      <Paper sx={{ padding: "16px", mb: 2 }}>
        <Typography fontSize={24} sx={{ mb: 2 }}>
          Results
        </Typography>
        <Box
          sx={{
            textAlign: "center",
            py: 4,
            color: "text.secondary",
            fontSize: "1.1rem",
          }}
        >
          No data to display
        </Box>
        <Box
          sx={{
            textAlign: "center",
            py: 4,
            color: "text.secondary",
            fontSize: "1.1rem",
          }}
        >
          Row count display: {meta_data.rowcount}
        </Box>
      </Paper>
    );
  }

  const convertToCSV = (data: any) => {
    if (!Array.isArray(data) || data.length === 0) return "";

    const keys =
      tableHeaders.length > 0
        ? tableHeaders.map((h) => h.key)
        : Object.keys(data[0]);

    const headerTitles =
      tableHeaders.length > 0 ? tableHeaders.map((h) => h.title) : keys;
    const header = headerTitles.join(",");
    const rows = data.map((row) =>
      keys
        ?.map((key) => {
          const rawValue = row[key];
          const value =
            rawValue === null || rawValue === undefined || rawValue === ""
              ? "N/A"
              : String(rawValue);

          // Escape quotes and wrap if needed
          const escaped = value.replace(/"/g, '""');
          return /[",\n]/.test(escaped) ? `"${escaped}"` : escaped;
        })
        .join(","),
    );
    return [header, ...rows].join("\r\n");
  };

  const fetchAndDownload = async () => {
    try {
      setLoading(true);
      await new Promise((resolve) => setTimeout(resolve, 1000));
      const timestamp = dayjs().format("MMM D, YYYY, h:mm A");
      const rawTableData = sortedRows;
      let blob: Blob;
      let fileNameBase = `DataStore_Query_Results_at_${timestamp}`;
      const csv = Array.isArray(rawTableData) ? convertToCSV(rawTableData) : "";
      blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const filename = `${fileNameBase}.csv`;
      triggerBrowserDownload(blob, filename);
    } catch (error: any) {
      store.dispatch(
        setDisplayErrorSnackBar({
          message: error?.parsedText || "Something went wrong from the getdata",
          type: "error",
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  const triggerBrowserDownload = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      {runQueryButtonLoading ? (
        <QueryResultsSkeleton rowCount={5} columnCount={4} />
      ) : (
        <Paper sx={{ padding: "16px", mb: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography fontSize={24}>Results</Typography>
            <Box sx={{ flexGrow: 1 }} />
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Total: {meta_data.rowcount.toLocaleString()} results
              </Typography>
              <Tooltip title="Export to CSV" arrow>
                <span>
                  <IconButton
                    aria-label="export to csv"
                    size="medium"
                    color="secondary"
                    loading={loading}
                    disabled={loading || sortedRows?.length === 0}
                    onClick={fetchAndDownload}
                  >
                    <DownloadIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>
          <TableContainer
            className="runQueryTable"
            component={Paper}
            sx={{
              boxShadow: "none",
              border: "1px solid rgba(224, 224, 224, 1)",
            }}
          >
            <Table size="medium" stickyHeader>
              <TableHead>
                <TableRow>
                  {tableHeaders.map((header) => (
                    <TableCell
                      key={header.key}
                      align={getCellAlignment(header.type)}
                      sx={{ fontWeight: "600" }}
                    >
                      <TableSortLabel
                        active={orderBy === header.key}
                        direction={orderBy === header.key ? order : "asc"}
                        onClick={() => handleSort(header.key)}
                      >
                        {header.title}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedRows?.map((row: any, rowIndex: number) => (
                  <TableRow
                    key={rowIndex}
                    sx={{
                      "& > *": {
                        borderBottom:
                          "1px solid rgba(224, 224, 224, 1) !important",
                      },
                      "&:hover": {
                        backgroundColor: "rgba(0, 0, 0, 0.04)",
                      },
                    }}
                  >
                    {tableHeaders.map((header) => (
                      <TableCell
                        key={header.key}
                        align={getCellAlignment(header.type)}
                        sx={{ py: "8px" }}
                      >
                        {formatCellValue(row[header.key], header.type)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Custom pagination pattern */}
          <Stack
            spacing={1}
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            padding={1}
          >
            {/* Left side - Page info */}
            <TablePagination
              rowsPerPageOptions={[]}
              component="div"
              count={sortedRows.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                ".MuiTablePagination-selectLabel, .MuiTablePagination-select, .MuiTablePagination-actions":
                  {
                    display: "none",
                  },
                ".MuiTablePagination-toolbar": {
                  paddingLeft: "16px",
                  minHeight: "auto",
                },
                ".MuiTablePagination-displayedRows": {
                  margin: 0,
                },
              }}
            />

            {/* Middle - Pagination controls */}
            <Pagination
              count={totalPages}
              page={page + 1}
              onChange={(_, value) => handleChangePage(null, value - 1)}
              showFirstButton
              showLastButton
              color="secondary"
              sx={{
                ".MuiPagination-ul": {
                  justifyContent: "center",
                },
              }}
              size="small"
            />

            {/* Right side - Rows per page selector */}
            <TablePagination
              rowsPerPageOptions={[5, 10, 15, 20]}
              component="div"
              count={sortedRows.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              classes={{
                displayedRows: "hidden-displayed-rows", // Apply the custom class
              }}
              sx={{
                ".MuiTablePagination-actions": {
                  display: "none",
                },
                ".MuiTablePagination-input": {
                  marginRight: "15px",
                },
              }}
            />
          </Stack>
        </Paper>
      )}
    </>
  );
};

export default DataStoreRequestRunQueryResults;
